import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, usePaginationFragment } from 'react-relay';
import {
  Card, CardFooter, CardHeader, Col, Row, Table,
} from 'reactstrap';

import { AdminTablePlaceholderRows } from 'src/components/Admin/AdminTablePlaceholder';
import BadgeActive from 'src/components/Badge/BadgeActive';
import NotFoundError from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/NotFoundError';
import PlaceholderErrorModal from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/PlaceholderErrorModal';
import LoadMoreButton from 'src/components/LoadMoreButton';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import { DEFAULT_LIST_PAGE_SIZE } from 'src/util/constants';
import { formatDateTimeFull } from 'src/util/i18n/helpers';
import logger from 'src/util/logger';

import AdminCommunityShowProperty from './AdminCommunityShowProperty';

const messages = defineMessages({
  paginationError: {
    id: 'admin.admin_community.admin_community_show.table.pagination_error.message',
    defaultMessage: 'Failed to load more properties. Please try again.',
  },
});

const AdminCommunityShowFragment = graphql`
  fragment AdminCommunityShow_community on Community
  @throwOnFieldError
  @refetchable(queryName: "AdminCommunityShowRefetchQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int" }
  )
  {
    uuid
    externalIdentifier
    title
    createdAt
    updatedAt
    active { start finish }
    properties(
      first: $count
      after: $cursor
    ) @connection(key: "AdminCommunityShow_properties") {
      edges {
        node {
          uuid
          title
          externalIdentifier
          active { start finish }
          address {
            line1 line2 city state postcode
          }
          primaryBillingPoints(first: 500) {
            edges {
              node {
                uuid
                identifier
                active { start finish }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * Skeleton rows rendered behind the pagination error modal, inside the loaded
 * properties table, to stand in for the page that failed to load.
 * @returns {React.ReactElement} Skeleton table rows.
 */
function PaginationErrorRows() {
  return <AdminTablePlaceholderRows rowCount={3} />;
}

/**
 * Admin view of a community with its paginated properties. Field errors throw
 * to the route's PlaceholderErrorBoundary; load-more failures show an inline
 * retry modal.
 * @param {object} props - Component props.
 * @param {object|null} props.community - Relay fragment reference for the community.
 * @returns {React.ReactComponentElement} AdminCommunityShow component.
 */
function AdminCommunityShow({ community: communityRef = null }) {
  const {
    data: community, loadNext, hasNext, isLoadingNext,
  } = usePaginationFragment(AdminCommunityShowFragment, communityRef);
  const [paginationError, setPaginationError] = useState(null);
  const intl = useIntl();

  const loadMore = () => {
    if (!hasNext || isLoadingNext) {
      return;
    }

    setPaginationError(null);
    loadNext(DEFAULT_LIST_PAGE_SIZE, {
      onComplete: (error) => {
        if (error) {
          logger.error({ error });
          setPaginationError(error);
        }
      },
    });
  };

  if (!community) {
    // A completed query without a community would otherwise never leave the spinner.
    throw new NotFoundError('AdminCommunityShow: the query returned no community');
  }

  const {
    uuid, externalIdentifier, active, createdAt,
    updatedAt, title, properties,
  } = community;
  const { start: activeStart, finish: activeFinish } = active;

  // Page title
  const pageTitle = intl.formatMessage(
    {
      id: 'admin.admin_community.admin_community_show.page.title',
      defaultMessage: 'Enosi Admin - Community - {title}',
    },
    { title },
  );

  // Breadcrumbs
  const breadcrumbAdmin = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.breadcrumbs.admin.label',
    defaultMessage: 'Admin',
  });
  const breadcrumbCommunities = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.breadcrumbs.communities.label',
    defaultMessage: 'Communities',
  });

  // Card headers
  const communityDetailsHeader = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.header',
    defaultMessage: 'Community details',
  });
  const propertiesHeader = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties.header',
    defaultMessage: 'Properties',
  });

  // Community details table headers
  const headerActiveStatus = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.header.active_status.label',
    defaultMessage: 'Active status',
  });
  const headerId = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.header.id.label',
    defaultMessage: 'ID',
  });
  const headerExtId = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.header.external_identifier.label',
    defaultMessage: 'Ext ID',
  });
  const headerExtIdCommunityDetailsTableTitle = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.external_identifier.abbr.title',
    defaultMessage: 'External identifier',
  });
  const headerStart = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.header.start.label',
    defaultMessage: 'Start',
  });
  const headerFinish = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.header.finish.label',
    defaultMessage: 'Finish',
  });
  const headerTitle = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.header.title.label',
    defaultMessage: 'Title',
  });
  const headerCreatedAt = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.header.created_at.label',
    defaultMessage: 'Created at',
  });
  const headerUpdatedAt = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.header.updated_at.label',
    defaultMessage: 'Updated at',
  });

  // Properties table headers
  const headerPropertiesTableTitle = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties.table.header.title.label',
    defaultMessage: 'Title',
  });
  const headerPropertiesTableId = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties.table.header.id.label',
    defaultMessage: 'ID',
  });
  const headerExtIdPropertiesTableTitle = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties.table.external_identifier.abbr.title',
    defaultMessage: 'External identifier',
  });
  const headerPropertiesTableExtId = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties.table.header.external_identifier.label',
    defaultMessage: 'Ext ID',
  });
  const headerPropertiesTableActive = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties.table.header.active.label',
    defaultMessage: 'Active',
  });

  const headerPropertiesTablePrimaryBillingPoints = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties.table.header.primary_billing_points.label',
    defaultMessage: 'Primary Billing Points',
  });

  // Load more button
  const copyLoadMore = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties_list.load_more_button.state_load_more.label',
    defaultMessage: 'Load more...',
  });
  const copyNoMore = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties_list.load_more_button.state_no_more.label',
    defaultMessage: 'No more properties to load',
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbCommunities, path: '/admin/communities' },
          { name: title },
        ]}
      />

      <h1>
        {title}
      </h1>

      <Row className="clear-both">
        <Col xs={12} sm={6} md={4}>
          <Card>
            <CardHeader>
              {communityDetailsHeader}
            </CardHeader>
            <Table responsive>
              <tbody>
                <tr>
                  <th scope="row" className="text-right">{headerActiveStatus}</th>
                  <td>
                    <BadgeActive start={activeStart} finish={activeFinish} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerId}</th>
                  <td>
                    <UUID uuid={uuid} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">
                    <abbr title={headerExtIdCommunityDetailsTableTitle}>{headerExtId}</abbr>
                  </th>
                  <td>{externalIdentifier}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerStart}</th>
                  <td>
                    {activeStart
                    && formatDateTimeFull(intl, activeStart)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerFinish}</th>
                  <td>
                    {activeFinish
                    && formatDateTimeFull(intl, activeFinish)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerTitle}</th>
                  <td>{title}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerCreatedAt}</th>
                  <td>
                    {createdAt
                      && formatDateTimeFull(intl, createdAt)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerUpdatedAt}</th>
                  <td>
                    {updatedAt
                    && formatDateTimeFull(intl, updatedAt)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={8}>
          <Card>
            <CardHeader>
              {propertiesHeader}
            </CardHeader>
            <Table responsive>
              <thead>
                <tr>
                  <th scope="col">{headerPropertiesTableTitle}</th>
                  <th scope="col">{headerPropertiesTableId}</th>
                  <th scope="col">
                    <abbr title={headerExtIdPropertiesTableTitle}>
                      {headerPropertiesTableExtId}
                    </abbr>
                  </th>
                  <th scope="col">
                    {headerPropertiesTablePrimaryBillingPoints}
                  </th>
                  <th scope="col">{headerPropertiesTableActive}</th>
                </tr>
              </thead>
              <tbody>
                {properties && properties.edges.map((el) => (
                  <AdminCommunityShowProperty property={el.node} key={el.node?.uuid} />
                ))}
                {/* The modal renders through a portal; only its skeleton rows render here. */}
                {paginationError && (
                  <PlaceholderErrorModal
                    bodyText={intl.formatMessage(messages.paginationError)}
                    error={paginationError}
                    onCancelFunc={() => setPaginationError(null)}
                    onRetryFunc={loadMore}
                    placeholderComponent={PaginationErrorRows}
                    retryDisabled={isLoadingNext}
                  />
                )}
              </tbody>
            </Table>
            <CardFooter>
              <LoadMoreButton
                copyLoadMore={copyLoadMore}
                copyNoMore={copyNoMore}
                onClick={loadMore}
                hasNext={hasNext}
              />
            </CardFooter>
          </Card>
        </Col>
      </Row>
    </>
  );
}

AdminCommunityShow.propTypes = {
  community: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default AdminCommunityShow;
